/**
 * @fileoverview Download CTA — large gradient card with a copy pitch, the
 * Google Play + App Store buttons, a "sign in to admin" secondary CTA and
 * a stylised phone frame on the right (desktop only) that shows a real
 * match-detail screenshot. `PhoneFrame` is the per-CTA presentational
 * helper, `StoreButton` the shared badge-style store link.
 *
 * The store buttons replaced a direct "Download APK" button, so the
 * sideload instructions that sat under it are gone too — they described a
 * flow visitors no longer go through. The APK itself is untouched and is
 * still linked from the hero CTA and the footer.
 */
import { Link } from 'react-router-dom'
import { Play, Smartphone } from 'lucide-react'
import { IconBrandAppleFilled, IconBrandGooglePlay } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { APP_STORE_URL, HERO_IMAGE, MOBILE_SHOTS, PLAY_STORE_URL } from '../constants'

export function DownloadCta() {
  return (
    <section id="download" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <Card className="overflow-hidden border-primary/30 bg-gradient-to-br from-primary/8 via-background to-background">
        <CardContent className="grid items-center gap-8 p-8 lg:grid-cols-[2fr_1fr] lg:p-12">
          <div>
            <Badge variant="secondary" className="mb-3 gap-1.5">
              <Smartphone className="h-3.5 w-3.5" />
              Android &amp; iOS
            </Badge>
            <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
              Score on the field, even when the wifi isn't
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              The Future Protea mobile app handles live scoring, push
              notifications, tournament dashboards and offline-tolerant
              ball entry. Install it on any device the assigned feeder
              brings to the ground.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <StoreButton
                href={PLAY_STORE_URL}
                icon={<IconBrandGooglePlay className="size-6" />}
                eyebrow="Get it on"
                store="Google Play"
              />
              <StoreButton
                href={APP_STORE_URL}
                icon={<IconBrandAppleFilled className="size-6" />}
                eyebrow="Download on the"
                store="App Store"
                variant="outline"
              />
              <Button asChild size="lg" variant="ghost" className="gap-2">
                <Link to="/login">
                  <Play className="h-4 w-4" />
                  Sign in to the admin
                </Link>
              </Button>
            </div>
          </div>

          <PhoneFrame />
        </CardContent>
      </Card>
    </section>
  )
}

/**
 * Store badge — the two-line "Get it on / Google Play" lockup the app
 * stores use, rebuilt on the site's own `Button` so it inherits the
 * palette, radius, focus ring and dark-mode handling instead of dropping
 * a foreign PNG badge into the layout. Solid (primary) reads as the lead
 * action, `outline` as its equal-weight sibling.
 *
 * Opens in a new tab because it leaves the site; `rel="noopener"` keeps
 * the store page from reaching back through `window.opener`. The label is
 * set explicitly with `aria-label` because the two stacked spans would
 * otherwise be announced run together as "Get it onGoogle Play".
 */
function StoreButton({
  href,
  icon,
  eyebrow,
  store,
  variant = 'default',
}: {
  href: string
  icon: React.ReactNode
  eyebrow: string
  store: string
  variant?: 'default' | 'outline'
}) {
  return (
    <Button
      asChild
      size="lg"
      variant={variant}
      className={cn(
        'h-14 gap-3 rounded-xl px-5 shadow-md transition-transform hover:-translate-y-0.5',
        // `text-foreground hover:text-foreground` is load-bearing, not
        // decoration. The `outline` variant ships `hover:text-accent-foreground`,
        // and once a visitor picks a palette the provider writes a near-black
        // `--accent-foreground` (oklch(0.208 …)) inline for dark mode. Paired
        // with the dark `hover:bg-primary/5` below — which replaces the
        // variant's light `hover:bg-accent` — the label turned black-on-black
        // and vanished on hover. Pinning it to `--foreground` keeps the text
        // readable against both surfaces, in either theme and any palette.
        variant === 'outline' &&
          'border-primary/40 text-foreground hover:border-primary/60 hover:bg-primary/5 hover:text-foreground',
      )}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${eyebrow} ${store}`}
      >
        {icon}
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[10px] font-medium uppercase tracking-wider opacity-80">
            {eyebrow}
          </span>
          <span className="text-base font-semibold">{store}</span>
        </span>
      </a>
    </Button>
  )
}

/**
 * Stylised Android phone frame for the download CTA. Shows an actual
 * match-detail screenshot from the live app (mobile2) so the CTA section
 * doubles as proof that the download lands on a working, branded
 * experience — not a mocked-up placeholder.
 */
function PhoneFrame() {
  return (
    <div className="relative mx-auto hidden w-56 lg:block">
      {/* Soft halo behind so the device pops off the gradient card. */}
      <div className="absolute inset-0 -z-10 scale-110 bg-[radial-gradient(circle_at_center,oklch(0.70_0.18_45/.35),transparent_70%)] blur-2xl" />

      {/* Device chassis — 10px bezel + rounded corners + speaker bar + lens. */}
      <div className="relative w-full overflow-hidden rounded-[2.5rem] border-[10px] border-foreground/90 bg-foreground/90 shadow-2xl">
        <div className="absolute left-1/2 top-1.5 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-foreground/40" />
        <div className="absolute right-3 top-1.5 z-10 h-1.5 w-1.5 rounded-full bg-foreground/60 ring-1 ring-background/20" />
        <img
          src={MOBILE_SHOTS[1]?.src ?? HERO_IMAGE}
          alt="Future Protea mobile app — match detail screen"
          className="block h-auto w-full rounded-[1.6rem]"
          loading="lazy"
        />
      </div>
    </div>
  )
}
